import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

interface StopEventRunConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isStopping: boolean;
  /**
   * Count of ALL the run's sessionRuns — finished ones included, because
   * the backend retro-cancels and reverts every sibling under a stopped
   * run, not just the ones still working. Counts-only, no per-cell
   * breakdown. Omit when the caller has no sessionRuns available (the
   * sidebar background tasks surface, which renders outside
   * `SessionRunsProvider`) or when the list is still empty during the
   * dispatch window (fan-out hasn't written rows yet, so a zero would
   * wrongly announce a no-op); the dialog then falls back to count-free
   * copy.
   */
  sampleCount?: number;
  onConfirm: () => void;
}

/**
 * Confirm dialog for stopping a full workpaper regeneration (see
 * `stopRequiresConfirmation`); column and sample reruns stop without one.
 * Stopping is deliberately "undo the whole run": the backend reverts every
 * sample the run touched to its pre-run state, samples that already
 * finished included, keeping only cells a user edited or locked. The copy
 * says so outright. Shaped like `DropAllDocsConfirmDialog` minus the
 * type-to-confirm input.
 */
export function StopEventRunConfirmDialog({
  open,
  onOpenChange,
  isStopping,
  sampleCount,
  onConfirm,
}: StopEventRunConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Stop this workpaper run?</AlertDialogTitle>
          <AlertDialogDescription>
            {sampleCount !== undefined
              ? `All ${sampleCount} sample${sampleCount !== 1 ? "s" : ""} in this run`
              : "Every sample in this run"}{" "}
            will revert to the pre-run state, including samples that already
            finished. Cells a user edited or locked are kept.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isStopping}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={isStopping}
            disabled={isStopping}
          >
            Stop and Revert
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
